import { Injectable } from '@angular/core';
import UsIndexTopConstituentsQuery from '../../graphql/us-index-top-constituents.graphql';
import { BehaviorSubject, forkJoin, Observable, Subject } from 'rxjs';
import {
  FundId,
  HeaderRendererComponent,
  ShareClassCode,
  StickyTab,
} from '@frk/eds-components';
import { ToggleOption } from '@types';
import { IndexesTopConstituentsService } from '../../services/indexes-top-constituents.service';
import {
  IndexProduct,
  IndexTopholding,
} from '../../types/model/index-top-constituent';
import {
  ConstituentTimePeriodData,
  USIndexTopConstituentsBRConfig,
  USIndexTopConstituentsState,
  UsIndexTopConstituentsTimePeriodData,
  USIndexTopConstituentsView,
} from '../../types/us-top-constituent.type';
import { US_INDEX_TOP_CONSTITUENT_INITIAL_STATE } from '../us-index-top-constituents.config';
import { ColDef } from '@ag-grid-community/core';
import { catchError, map, take } from 'rxjs/operators';
import { CaveatService } from '@products/caveats/services/caveat.service';
import { TranslateService } from '@shared/translate/translate.service';
import cloneDeep from 'lodash/cloneDeep';
@Injectable({
  providedIn: 'root',
})
export class UsIndexTopConstituentsService {
  private state: USIndexTopConstituentsState;
  private brConfig: USIndexTopConstituentsBRConfig;
  private brLabels: any;
  private UsIndexTopConstituentsState$: Subject<USIndexTopConstituentsState>;
  private fundTabs: StickyTab[] = [];
  private fundName: string;
  private ticker: string;
  private fundId: FundId;
  private showTopConstituents$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );

  constructor(
    private topConstituentService: IndexesTopConstituentsService,
    private caveatService: CaveatService,
    private translateService: TranslateService
  ) {
    this.state = US_INDEX_TOP_CONSTITUENT_INITIAL_STATE;
    this.UsIndexTopConstituentsState$ = new Subject<USIndexTopConstituentsState>();
  }

  populate(config: any, labels: any): void {
    // Save brConfig for custom  API call.
    this.brConfig = config;
    this.brLabels = labels;
    this.fundName = this.brConfig?.fundName;
    this.ticker = this.brConfig?.ticker;
    this.fundId = config.fundId as FundId;
    this.topConstituentService
      .register(UsIndexTopConstituentsQuery, {
        fundId: config.fundId as FundId,
        shareClassCode: config.shareClassCode as ShareClassCode,
      })
      .subscribe(
        (data: IndexProduct) => {
          this.mapState(data);
        },
        (err) => {
          this.mapState(null);
        }
      );
    this.fundTabs = this.brConfig?.tabs.filter((tab) => ({
      id: tab.id,
      title: tab.label,
    }));
    if (
      !!this.fundTabs.find(
        (tab: StickyTab): boolean => tab.id === 'constituents'
      )
    ) {
      this.showTopConstituents$.next(true);
    }
  }

  /**
   * Return state data
   * @returns Observable<USIndexTopConstituentsState>
   */
  getUsIndexTopConstituentsStateState$(): Observable<USIndexTopConstituentsState> {
    return this.UsIndexTopConstituentsState$.asObservable();
  }

  public getTopConstituents$(): Observable<boolean> {
    return this.showTopConstituents$.asObservable();
  }

  public replaceWithAmpersand(data: string): string {
    const regex = /&amp;/gi;
    return data.replace(regex, '&');
  }

  /**
   * get the proximal caveats for a particular placement
   */
  private getProximalCaveat$(placement: string): Observable<string[][]> {
    return this.caveatService.getProximals$(placement).pipe(
      take(1),
      map((caveatContext) => {
        const caveat = [];
        const caveatData = caveatContext.map((data) => data.split('\n'));

        caveatData[0]?.forEach((data: any) => {
          let trimedData = data.replace(/(<([^>]+)>)/gi, '');
          trimedData = this.replaceWithAmpersand(trimedData);

          if (trimedData.length > 0) {
            caveat.push([trimedData]);
          }
        });

        return caveat;
      }),
      catchError(() => {
        return [];
      })
    );
  }

  public downlaodExcel(): void {
    forkJoin([
      this.getProximalCaveat$('AllocationWeightsExcelBottom'),
      this.getProximalCaveat$('AllocationWeightsExcelTop'),
    ])
      .pipe(take(1))
      .subscribe(([bottomCaveat, topCaveat]) => {
        const excelTableData = this.getExcelTableData(this.state);

        import('xlsx').then((xlsx) => {
          const merges = [];
          const maxColMerge = 200; // *** Change here for number of columns to be merged for caveats. ***

          // Step 1: add top caveat line,
          const workSheet = xlsx.utils.aoa_to_sheet(topCaveat, {
            cellStyles: true,
          });

          // Step 2: For each top caveat line, merge it to single cell and add to 'merges' array.
          topCaveat.forEach((caveat, index) => {
            merges.push({
              s: {
                r: index,
                c: 0,
              },
              e: {
                r: index,
                c: maxColMerge,
              },
            });
          });

          // Step 3: Add as of date.
          const asOfDate = [
            this.translateService.instant('products.as-of'),
            this.state.data.constituentDataByTimePeriod.monthly.asOfDate,
          ];
          xlsx.utils.sheet_add_aoa(workSheet, [asOfDate], {
            origin: 'A' + (topCaveat.length + 3),
          });

          // Step 4: Add TableData.
          xlsx.utils.sheet_add_aoa(workSheet, excelTableData, {
            origin: 'A' + (topCaveat.length + asOfDate.length + 5),
          });

          // Step 5: Calculate bottom caveat start row.
          const bottomCaveatRowStart =
            topCaveat.length + asOfDate.length + excelTableData.length + 7;

          // Step 6: Add bottom caveats starting from above calculated start row.
          xlsx.utils.sheet_add_aoa(workSheet, bottomCaveat, {
            origin: 'A' + bottomCaveatRowStart,
          });

          // Step 7: For each bottom caveat, again, merge it to single cell and add to 'merges' array.
          bottomCaveat.forEach((caveat, index) => {
            merges.push({
              s: {
                r: bottomCaveatRowStart + index,
                c: 0,
              },
              e: {
                r: bottomCaveatRowStart + index,
                c: maxColMerge,
              },
            });
          });

          // Step 7: Finally, configure the merges & column width and append sheet to the workbook.
          workSheet['!merges'] = merges;

          const wscols = [
            { wpx: 140 },
            { wpx: 180 },
            { wpx: 120 },
            { wpx: 120 },
          ];
          workSheet['!cols'] = wscols;

          const workBook = xlsx.utils.book_new();

          this.ticker
            ? xlsx.utils.book_append_sheet(workBook, workSheet, this.ticker)
            : xlsx.utils.book_append_sheet(workBook, workSheet);

          xlsx.writeFile(
            workBook,
            `${this.fundName} ${this.brLabels?.allocation_weights_header_download_excel_monthly}.${this.brLabels['us-index-download-file-extension']}`
          );
        });
      });
  }

  private getExcelTableData(state: USIndexTopConstituentsState): string[][] {
    const finalTableData: string[][] = [];
    finalTableData.push([
      this.brLabels?.isin,
      this.brLabels['us-index-top-constituent-table-header-name'],
      this.brLabels?.country_code,
      this.brLabels['us-index-top-constituent-table-header-weight-percent'],
    ]);
    const sortedData = state.data.excelData
      .sort((a, b) => {
        const isNotEqual = a.breakdownPercentage.localeCompare(
          b.breakdownPercentage
        );
        if (!isNotEqual) {
          return a.issuerName.localeCompare(b.issuerName);
        }
        return isNotEqual;
      })
      .reverse();
    sortedData.forEach((record) => {
      const rowData: string[] = [];
      rowData.push(record.isin);
      rowData.push(record.issuerName);
      rowData.push(record.countryCode);
      rowData.push(record.breakdownPercentage);
      finalTableData.push(rowData);
    });
    return finalTableData;
  }

  /**
   * Assign state and process data.
   * @param  portfolio Porfolio data
   */
  mapState(product: IndexProduct): void {
    if (product?.portfolio?.topHoldings?.length) {
      const dailyData = this.buildTimePeriodData(
        product.portfolio.topHoldings,
        USIndexTopConstituentsView.DAILY
      );
      const monthlyData = this.buildTimePeriodData(
        product.portfolio.topHoldings,
        USIndexTopConstituentsView.MONTHLY
      );
      const quarterlyData = this.buildTimePeriodData(
        product.portfolio.topHoldings,
        USIndexTopConstituentsView.QUARTERLY
      );
      // Check for data sequantially for default selection top constituent view
      if (!dailyData?.data?.length) {
        if (monthlyData?.data?.length) {
          this.state.config.selectedTopConstituentView =
            USIndexTopConstituentsView.MONTHLY;
        } else {
          this.state.config.selectedTopConstituentView =
            USIndexTopConstituentsView.QUARTERLY;
        }
      }
      this.state.data.constituentDataByTimePeriod = {
        daily: dailyData,
        monthly: monthlyData,
        quarterly: quarterlyData,
      };

      this.state.data.excelData = cloneDeep(product.portfolio?.dailyholdings);

      const selectedTimePeriodData = this.tableDataByTimePeriod(
        this.state.data.constituentDataByTimePeriod,
        this.state.config.selectedTopConstituentView
      );

      this.state.config.tableConfig = {
        title: this.getTitleBasedOnFundId(this.fundId),
        header: this.getDefaultColDefs(),
        tooltip: this.brLabels[`us-index-constituent-header-tooltip`],
        proximalPlacementTop: 'IndexConstituentProximalTop',
        proximalPlacementBottom: 'IndexConstituentProximalBottom',
        caveatPlacement: 'USIndexConstituents',
        body: selectedTimePeriodData.data,
        asOfDate: selectedTimePeriodData.asOfDate,
        label: this.brLabels[
          `us-index-constituent-view-toggle-${this?.state?.config?.selectedTopConstituentView?.toLowerCase()}`
        ],
        updateFrequency: this.getUpdateFrequencyLabel(
          this?.state?.config?.selectedTopConstituentView
        ),
      };

      this.state.config.topConstituentViews = this.getPerformanceViews(
        this.state.data.constituentDataByTimePeriod
      );
    }
    this.state.hasLoadedFullFeed = true;
    this.UsIndexTopConstituentsState$.next(this.state);
  }

  /**
   *
   * @param fundId FundId
   * @returns string
   */
  private getTitleBasedOnFundId(fundId: FundId): string {
    return this.brLabels[`us-index-top-ten-constituent-title-${fundId}`];
  }

  /**
   * Table data based on time periods
   * @param data IndexTopholding[]
   * @param timePeriodView USIndexTopConstituentsView
   * @returns ConstituentTimePeriodData
   */
  private buildTimePeriodData(
    data: IndexTopholding[],
    timePeriodView: USIndexTopConstituentsView
  ): ConstituentTimePeriodData {
    const timePeriodData = data.filter(
      (holding) => holding.frequency === timePeriodView
    );
    return {
      data: timePeriodData,
      asOfDate: timePeriodData?.length ? timePeriodData[0]?.asOfDate : null,
    };
  }

  /**
   * Return table data based on toggle option selected
   * @param timePeriodData UsIndexTopConstituentsTimePeriodData
   * @param selectedTimePeriod USIndexTopConstituentsView
   * @returns ConstituentTimePeriodData
   */
  private tableDataByTimePeriod(
    timePeriodData: UsIndexTopConstituentsTimePeriodData,
    selectedTimePeriod: USIndexTopConstituentsView
  ): ConstituentTimePeriodData {
    if (selectedTimePeriod === USIndexTopConstituentsView.DAILY) {
      return timePeriodData.daily;
    } else if (selectedTimePeriod === USIndexTopConstituentsView.MONTHLY) {
      return timePeriodData.monthly;
    } else {
      return timePeriodData.quarterly;
    }
  }

  /**
   * Generate toggle buttons
   * @param timePeriodData Time Period Data
   * @returns ToggleOption[]
   */
  private getPerformanceViews(
    timePeriodData: UsIndexTopConstituentsTimePeriodData
  ): ToggleOption[] {
    const perfViewToggleoptions: ToggleOption[] = [];

    Object.values(USIndexTopConstituentsView).forEach((constituentView) => {
      perfViewToggleoptions.push({
        text: this.brLabels[
          `us-index-constituent-view-toggle-${constituentView.toLowerCase()}`
        ],
        active:
          this.state.config.selectedTopConstituentView === constituentView,
        value: constituentView,
        disabled: !this.tableDataByTimePeriod(timePeriodData, constituentView)
          ?.data?.length,
      });
    });
    return perfViewToggleoptions;
  }

  /**
   * Handle Toggle change
   * @param option ToggleOption
   *
   */
  toggleChanged(option: ToggleOption): void {
    const selectedTimePeriodData = this.tableDataByTimePeriod(
      this.state.data.constituentDataByTimePeriod,
      option.value as USIndexTopConstituentsView
    );
    this.state.config.tableConfig = {
      ...this.state.config.tableConfig,
      body: selectedTimePeriodData.data,
      asOfDate: selectedTimePeriodData.asOfDate,
      label: option?.text,
      updateFrequency: this.getUpdateFrequencyLabel(
        option?.value as USIndexTopConstituentsView
      ),
    };
    this.state.config.selectedTopConstituentView = option.value as USIndexTopConstituentsView;
    this.UsIndexTopConstituentsState$.next(this.state);
  }

  /**
   *
   * @param selectedTimePeriod current selected time period
   * @returns Label string
   */
  private getUpdateFrequencyLabel(
    selectedTimePeriod: USIndexTopConstituentsView
  ): string {
    return this.brLabels[
      `us-index-constituent-view-updated-freq-${selectedTimePeriod.toLowerCase()}`
    ];
  }

  /**
   * Get the col defs for ag grid used in top-constituent component
   * @returns ColDef[]
   */
  getDefaultColDefs(): ColDef[] {
    return [
      {
        colId: 'CONSTITUENT-NAME',
        headerName: this.brLabels['us-index-top-constituent-table-header-name'],
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          isAlignRight: false,
          footnotePlacement: `USIndexConstituentName`,
          tooltipContent: this.brLabels[
            'us-index-top-constituent-table-header-name-tooltip'
          ],
        },
        valueGetter: (params) => {
          const holding: IndexTopholding = params.data;
          return holding.issuerName;
        },
        autoHeight: true,
        sortable: true,
        wrapText: true,
        minWidth: 140,
        flex: 1,
        cellStyle: {
          whitespace: 'normal',
        },
        suppressMovable: true,
      },
      {
        colId: 'WEIGHT-PERCENTAGE',
        headerName: this.brLabels[
          'us-index-top-constituent-table-header-weight-percent'
        ],
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          isAlignRight: false,
          footnotePlacement: `USIndexConstituentWeight`,
          tooltipContent: this.brLabels[
            'us-index-top-constituent-table-header-weight-percent-tooltip'
          ],
        },
        valueGetter: (params) => {
          const holding: IndexTopholding = params.data;
          return holding.breakdownPercentage;
        },
        autoHeight: true,
        sortable: true,
        wrapText: true,
        sort: 'desc',
        flex: 1,
        cellStyle: {
          whitespace: 'normal',
        },
        minWidth: 60,
      },
    ];
  }
}
